import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  FilterBar,
  PageHeader,
  SearchField,
  Skeleton,
  Table,
} from "@evolua/ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useBatchHrDocuments,
  useDistributeHrDocument,
  useHrDocumentAccesses,
  useHrDocuments,
} from "../api/use-hr.js";

const RH_DEPARTMENT_ID = "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb";

/** Gestão documental do RH (FASE-06-T51). */
export function DocumentosRhPage() {
  const [q, setQ] = useState("");
  const [onlyExpired, setOnlyExpired] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(onlyExpired ? { expired: true } : {}),
    }),
    [q, onlyExpired],
  );

  const query = useHrDocuments(filters);
  const batch = useBatchHrDocuments();
  const distribute = useDistributeHrDocument();
  const accesses = useHrDocumentAccesses(selectedId);

  const correlationId =
    query.error instanceof EvoluaApiError ? query.error.problem.traceId : undefined;

  async function onBatchDistribute() {
    setActionError(null);
    try {
      await batch.mutateAsync({
        items: [
          {
            name: `Comunicado RH ${new Date().toISOString().slice(0, 10)}`,
            category: "POLICY",
            content: "Documento distribuído em lote (demo T51).",
          },
        ],
        distributeDepartmentId: RH_DEPARTMENT_ID,
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha no lote");
    }
  }

  async function onDistribute(id: string) {
    setActionError(null);
    try {
      await distribute.mutateAsync({
        id,
        body: { departmentId: RH_DEPARTMENT_ID },
      });
      setSelectedId(id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha na distribuição");
    }
  }

  if (query.isPending) {
    return <Skeleton height="8rem" label="Documentos RH" />;
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Documentos (RH)"
        lead="Upload, categorização, distribuição por departamento e rastreio de quem acessou. Documentos vencidos ficam sinalizados."
      />

      <FilterBar>
        <SearchField
          label="Buscar"
          placeholder="Buscar…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Buscar documentos"
        />
        <Checkbox
          label="Só vencidos"
          checked={onlyExpired}
          onChange={(e) => setOnlyExpired(e.target.checked)}
        />
        <Button
          type="button"
          size="sm"
          disabled={batch.isPending}
          onClick={() => void onBatchDistribute()}
        >
          {batch.isPending ? "Distribuindo…" : "Lote → depto RH"}
        </Button>
        <Badge tone={query.data.expiredCount > 0 ? "warning" : "neutral"}>
          {query.data.total} doc(s) · {query.data.expiredCount} vencido(s)
        </Badge>
      </FilterBar>

      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      {query.data.data.length === 0 ? (
        <EmptyState
          title="Nenhum documento"
          description="Cadastre ou distribua documentos para a equipe."
        />
      ) : (
        <Table
          caption="Documentos"
          getRowId={(doc) => doc.id}
          columns={[
            { id: "name", header: "Documento", cell: (doc) => doc.name },
            {
              id: "category",
              header: "Categoria",
              cell: (doc) => (
                <>
                  <Badge tone="neutral">{doc.category}</Badge>{" "}
                  {doc.expired ? <Badge tone="warning">Vencido</Badge> : null}
                </>
              ),
            },
            {
              id: "meta",
              header: "Distribuição",
              cell: (doc) => (
                <span className="evolua-muted">
                  {doc.employeeIds.length} vínculo(s) · {doc.accessCount} acesso(s)
                  {doc.expiresAt ? ` · validade ${doc.expiresAt}` : ""}
                </span>
              ),
            },
            {
              id: "actions",
              header: "Ações",
              cell: (doc) => (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={distribute.isPending}
                    onClick={() => void onDistribute(doc.id)}
                  >
                    Distribuir RH
                  </Button>{" "}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setSelectedId(doc.id)}
                  >
                    Acessos
                  </Button>
                </>
              ),
            },
          ]}
          rows={query.data.data}
        />
      )}

      {selectedId ? (
        <section className="evolua-stack evolua-stack--3">
          <h2 className="evolua-section-title">Acessos · {selectedId}</h2>
          {accesses.isPending ? (
            <Skeleton height="3rem" />
          ) : accesses.data && accesses.data.total > 0 ? (
            <Table
              caption="Acessos"
              getRowId={(a) => a.id}
              columns={[
                { id: "at", header: "Quando", cell: (a) => a.at },
                { id: "subject", header: "Subject", cell: (a) => a.subject },
                { id: "action", header: "Ação", cell: (a) => a.action },
              ]}
              rows={accesses.data.data}
            />
          ) : (
            <p className="evolua-muted">
              Nenhum acesso ainda. Quando o colaborador baixar em /documentos, aparece
              aqui.
            </p>
          )}
        </section>
      ) : null}

      <p className="evolua-mt">
        <Link to="/documentos">Portal do colaborador →</Link>
      </p>
    </>
  );
}
